// Colour arithmetic shared by the dashboard accent (dashboardChrome) and the
// visual theme (surfaceTheme / plotlyTheme). One copy, so "reads on this
// surface" means the same thing everywhere.

export type RGB = [number, number, number]

export const WHITE: RGB = [255, 255, 255]
export const BLACK: RGB = [0, 0, 0]

const HEX = /^#(?:[0-9a-f]{3}|[0-9a-f]{4}|[0-9a-f]{6}|[0-9a-f]{8})$/i

export function isHexColor(s: string): boolean {
  return HEX.test(s.trim())
}

/** '#abc' / '#aabbcc' (lower-case, alpha dropped). */
export function expandHex(s: string): string {
  let h = s.trim().slice(1)
  if (h.length === 3 || h.length === 4) h = h.slice(0, 3).split('').map((c) => c + c).join('')
  return `#${h.slice(0, 6).toLowerCase()}`
}

const NAMED: Record<string, [number, number, number, number]> = {
  white: [255, 255, 255, 1], black: [0, 0, 0, 1], transparent: [0, 0, 0, 0],
  gray: [128, 128, 128, 1], grey: [128, 128, 128, 1],
}

/** A colour with its alpha (1 when none is given), or null when unreadable
 *  (a gradient, a CSS variable, a name we do not know). */
export function parseRgba(s: string | null | undefined): [number, number, number, number] | null {
  const t = (s || '').trim().toLowerCase()
  if (!t) return null
  if (NAMED[t]) return NAMED[t]
  if (HEX.test(t)) {
    let h = t.slice(1)
    if (h.length === 3 || h.length === 4) h = h.split('').map((c) => c + c).join('')
    const n = parseInt(h.slice(0, 6), 16)
    const a = h.length === 8 ? parseInt(h.slice(6, 8), 16) / 255 : 1
    return [(n >> 16) & 255, (n >> 8) & 255, n & 255, a]
  }
  const m = /^rgba?\(\s*([\d.]+)[\s,]+([\d.]+)[\s,]+([\d.]+)(?:[\s,/]+([\d.]+%?))?/i.exec(t)
  if (!m) return null
  let a = 1
  if (m[4] !== undefined) a = m[4].endsWith('%') ? parseFloat(m[4]) / 100 : parseFloat(m[4])
  return [Number(m[1]), Number(m[2]), Number(m[3]), a]
}

export function parseColor(s: string | null | undefined): RGB | null {
  const c = parseRgba(s)
  return c ? [c[0], c[1], c[2]] : null
}

export function toHex(c: RGB): string {
  return `#${c.map((v) => Math.round(Math.max(0, Math.min(255, v))).toString(16).padStart(2, '0')).join('')}`
}

export function mix(a: RGB, b: RGB, t: number): RGB {
  return [a[0] + (b[0] - a[0]) * t, a[1] + (b[1] - a[1]) * t, a[2] + (b[2] - a[2]) * t]
}

export function luminance(c: RGB): number {
  const [r, g, b] = c.map((v) => {
    const x = v / 255
    return x <= 0.03928 ? x / 12.92 : Math.pow((x + 0.055) / 1.055, 2.4)
  })
  return 0.2126 * r + 0.7152 * g + 0.0722 * b
}

export function contrast(a: RGB, b: RGB): number {
  const x = luminance(a)
  const y = luminance(b)
  return (Math.max(x, y) + 0.05) / (Math.min(x, y) + 0.05)
}

/** A surface this dark wants light text. */
export function isDarkColor(c: RGB): boolean {
  return luminance(c) < 0.4
}

/** Darken (light surface) or lighten (dark surface) until it reads at `min`:1. */
export function readableOn(c: RGB, surface: RGB, min = 4.5): RGB {
  const toward = isDarkColor(surface) ? WHITE : BLACK
  let out = c
  for (let i = 0; i < 20 && contrast(out, surface) < min; i++) out = mix(out, toward, 0.1)
  return out
}
